import { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Badge, Spinner } from "react-bootstrap";
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconCalendar,
  IconCrown,
  IconMessageCircle,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import PlanServices from "../../../services/PlanServices";

/**
 * Modal para cancelamento de plano.
 * Exibe preview do cancelamento e coleta motivo/feedback do usuário.
 */
const CancelPlanModal = ({ show, onHide, onSuccess }) => {
  // Estados de dados
  const [cancelPreview, setCancelPreview] = useState(null);

  // Estados de loading
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState(null);

  // Estados de resultado
  const [cancelResult, setCancelResult] = useState(null);
  const [cancelError, setCancelError] = useState(null);

  // Estados do formulário
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");

  // Motivos de cancelamento
  const CANCEL_REASONS = [
    { value: "", label: "Selecione um motivo..." },
    { value: "not_using", label: "Nao estou usando" },
    { value: "too_expensive", label: "Preco muito alto" },
    { value: "found_alternative", label: "Encontrei alternativa melhor" },
    { value: "technical_issues", label: "Problemas tecnicos" },
    { value: "missing_features", label: "Falta de funcionalidades" },
    { value: "temporary_pause", label: "Pausa temporaria" },
    { value: "other", label: "Outro" },
  ];

  // Carregar preview quando o modal abre
  useEffect(() => {
    if (show) {
      loadCancelPreview();
      // Reset states
      setReason("");
      setFeedback("");
      setCancelResult(null);
      setCancelError(null);
    }
  }, [show]);

  const loadCancelPreview = async () => {
    setLoadingPreview(true);
    setPreviewError(null);

    try {
      const response = await PlanServices.previewCancelPlan();
      if (response.data) {
        setCancelPreview(response.data);
      }
    } catch (error) {
      console.error("Erro ao carregar preview do cancelamento:", error);
      const errorData = error.response?.data;
      setPreviewError(
        errorData?.error || errorData?.message || "Erro ao carregar informacoes do cancelamento."
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirm = async () => {
    if (!reason) return;

    // Salvar dados do formulário antes de fechar o modal
    const savedReason = reason;
    const savedFeedback = feedback;
    const savedPreview = cancelPreview;

    // Fechar o modal do Bootstrap para evitar conflito com Swal
    onHide();

    // Aguardar um momento para o modal fechar completamente
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Exibir Swal para confirmação com input
    const result = await Swal.fire({
      title: "Confirmar Cancelamento",
      html: `
        <p class="text-danger mb-3">
          <strong>Esta acao e irreversivel!</strong>
        </p>
        <p class="mb-3">
          Para confirmar o cancelamento do seu plano, digite <strong>CONFIRMO</strong> no campo abaixo:
        </p>
      `,
      input: "text",
      inputPlaceholder: "Digite CONFIRMO",
      inputAttributes: {
        autocapitalize: "off",
        autocomplete: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Cancelar Plano",
      cancelButtonText: "Voltar",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value || value.toUpperCase() !== "CONFIRMO") {
          return "Digite CONFIRMO para confirmar o cancelamento";
        }
      },
    });

    if (!result.isConfirmed) return;

    // Mostrar loading no Swal
    Swal.fire({
      title: "Processando...",
      text: "Aguarde enquanto cancelamos seu plano.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const reasonText = CANCEL_REASONS.find((r) => r.value === savedReason)?.label || savedReason;
      const response = await PlanServices.cancelPlan(reasonText, savedFeedback || null);

      if (response.data?.success) {
        const accessMessage = response.data.access?.message || "";
        const expiresAt = response.data.access?.expires_at
          ? new Date(response.data.access.expires_at).toLocaleDateString("pt-BR")
          : "";

        await Swal.fire({
          icon: "success",
          title: "Plano Cancelado",
          html: `
            <p>${response.data.message}</p>
            ${accessMessage ? `<p class="text-muted">${accessMessage}</p>` : ""}
            ${expiresAt ? `<p><strong>Acesso ate:</strong> ${expiresAt}</p>` : ""}
            ${response.data.reactivation?.available ? `<p class="text-info small mt-3">${response.data.reactivation.message}</p>` : ""}
          `,
          confirmButtonColor: "#28a745",
        });

        // Chamar callback de sucesso
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      const errorData = error.response?.data;
      const errorMessage =
        errorData?.error || errorData?.message || "Erro ao cancelar plano. Tente novamente.";

      await Swal.fire({
        icon: "error",
        title: "Erro no Cancelamento",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handleClose = () => {
    if (cancelResult && onSuccess) {
      onSuccess();
    }
    setCancelResult(null);
    setCancelError(null);
    setCancelPreview(null);
    onHide();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    } catch {
      return "—";
    }
  };

  // Renderizar estado de carregamento
  if (loadingPreview) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="mb-0">Carregando informacoes do cancelamento...</p>
        </Modal.Body>
      </Modal>
    );
  }

  // Renderizar erro do preview
  if (previewError) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="d-flex align-items-center">
            <IconAlertTriangle size={28} className="text-danger me-2" />
            Erro
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">{previewError}</Alert>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Renderizar resultado de sucesso
  if (cancelResult) {
    return (
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton className="border-0 bg-warning">
          <Modal.Title className="d-flex align-items-center">
            <IconCheck size={28} className="me-2" />
            Plano Cancelado
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="warning" className="mb-4">
            <div className="d-flex align-items-center">
              <IconAlertTriangle size={24} className="me-3 flex-shrink-0" />
              <div>
                <strong>{cancelResult.message}</strong>
                {cancelResult.access?.message && (
                  <p className="mb-0 mt-1">{cancelResult.access.message}</p>
                )}
              </div>
            </div>
          </Alert>

          {/* Informações do acesso */}
          {cancelResult.access && (
            <div className="bg-light rounded p-4 mb-4">
              <h6 className="mb-3 d-flex align-items-center">
                <IconCalendar size={18} className="me-2" />
                Seu Acesso
              </h6>
              <div className="row g-3">
                <div className="col-6">
                  <small className="text-muted d-block">Status</small>
                  <Badge bg="warning" text="dark">
                    Ativo ate expiracao
                  </Badge>
                </div>
                <div className="col-6">
                  <small className="text-muted d-block">Acesso ate</small>
                  <strong>{formatDate(cancelResult.access.expires_at)}</strong>
                </div>
                {cancelResult.access.days_remaining !== undefined && (
                  <div className="col-12">
                    <small className="text-muted d-block">Dias restantes</small>
                    <strong>{cancelResult.access.days_remaining} dias</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Informação de reativação */}
          {cancelResult.reactivation?.available && (
            <Alert variant="info" className="mb-0">
              <IconMessageCircle size={18} className="me-2" />
              {cancelResult.reactivation.message}
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0">
          <Button variant="primary" onClick={handleClose} size="lg">
            <IconCheck size={18} className="me-2" />
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Verificar se pode cancelar
  if (cancelPreview && !cancelPreview.can_cancel) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="d-flex align-items-center">
            <IconAlertTriangle size={28} className="text-warning me-2" />
            Cancelamento Indisponivel
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            Nao e possivel cancelar seu plano no momento. Entre em contato com o suporte para mais
            informacoes.
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Renderizar modal de confirmação
  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <IconX size={28} className="text-danger me-2" />
          Cancelar Plano
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Aviso principal */}
        <Alert variant="danger" className="mb-4">
          <div className="d-flex align-items-start">
            <IconAlertTriangle size={24} className="me-3 flex-shrink-0 mt-1" />
            <div>
              <strong>Tem certeza que deseja cancelar?</strong>
              <p className="mb-0 mt-2">
                Apos o cancelamento, voce perdera acesso a todas as funcionalidades premium do seu
                plano.
              </p>
            </div>
          </div>
        </Alert>

        {/* Informações do plano atual */}
        {cancelPreview?.plan && (
          <div className="bg-light rounded p-4 mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <IconCrown size={18} className="me-2" />
              Seu Plano Atual
            </h6>
            <div className="row g-3">
              <div className="col-6">
                <small className="text-muted d-block">Plano</small>
                <strong className="text-primary">{cancelPreview.plan.name}</strong>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Valor mensal</small>
                <strong>{PlanServices.formatCurrency(cancelPreview.plan.monthly_value)}</strong>
              </div>
              {cancelPreview.access?.expires_at && (
                <div className="col-6">
                  <small className="text-muted d-block">Acesso ate</small>
                  <div className="d-flex align-items-center">
                    <IconCalendar size={16} className="me-1 text-muted" />
                    <strong>{formatDate(cancelPreview.access.expires_at)}</strong>
                  </div>
                </div>
              )}
              {cancelPreview.access?.days_remaining !== undefined && (
                <div className="col-6">
                  <small className="text-muted d-block">Dias restantes</small>
                  <strong>{cancelPreview.access.days_remaining} dias</strong>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Aviso do backend */}
        {cancelPreview?.warning?.message && (
          <Alert variant="warning" className="mb-4">
            <IconAlertTriangle size={18} className="me-2" />
            {cancelPreview.warning.message}
          </Alert>
        )}

        {/* Formulário */}
        <div className="mb-4">
          <Form.Group className="mb-3">
            <Form.Label>
              Motivo do cancelamento <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              isInvalid={cancelError && !reason}
            >
              {CANCEL_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Feedback adicional (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Conte-nos mais sobre o motivo do cancelamento..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
            <Form.Text className="text-muted">
              Seu feedback nos ajuda a melhorar a plataforma.
            </Form.Text>
          </Form.Group>
        </div>

        {/* Erro de cancelamento */}
        {cancelError && (
          <Alert variant="danger" className="mb-4">
            <IconAlertTriangle size={18} className="me-2" />
            {cancelError}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
          Voltar
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirm}
          disabled={!reason || loading}
          className="d-flex align-items-center"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Processando...
            </>
          ) : (
            <>
              <IconX size={18} className="me-2" />
              Cancelar Plano
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CancelPlanModal;
